import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getProviderConfig,
  updateProviderConfig,
  ProviderConfigMap,
  PROVIDER_CONFIG_CATEGORIES,
  CONFIG_LABELS,
  CONFIG_UNITS,
} from '@/services/api/providers';
import { toast } from '@/design-system/toaster';

interface ProviderConfigTabProps {
  providerId: number | string;
}

const SELECT_OPTIONS: Record<string, { value: string; label: string }[]> = {
  CLIENT_SERVICE: [
    { value: 'EXPRESS', label: 'Express' },
    { value: 'FLEET+', label: 'Fleet+' },
    { value: 'FLEX', label: 'Flex' },
  ],
  PORTAL_MULTI_STOP_OPTIONS: [
    { value: 'MULTI_DROP-OFF', label: 'Multi Drop-Off' },
    { value: 'MULTI_PICK-UP', label: 'Multi Pick-Up' },
    { value: 'MULTI_DROP-OFF_AND_PICK-UP', label: 'Multi Drop-Off and Pick-Up' },
  ],
  PORTAL_COD_MODE: [
    { value: 'OFF', label: 'Off' },
    { value: 'COD', label: 'COD' },
    { value: 'CASH_ADVANCE', label: 'Cash Advance' },
  ],
  SHIPPING_DOC_SIGNATURE_OPTIONS: [
    { value: 'RECIPIENT', label: 'Recipient' },
    { value: 'SENDER', label: 'Sender' },
  ],
};

export const ProviderConfigTab: React.FC<ProviderConfigTabProps> = ({
  providerId,
}) => {
  const [config, setConfig] = useState<ProviderConfigMap>({});
  const [loading, setLoading] = useState(true);
  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getProviderConfig(providerId);
      setConfig(data || {});
    } catch (err: any) {
      toast.error('Failed to load provider configurations');
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const saveToServer = async (key: string, value: any) => {
    try {
      const stringValue = Array.isArray(value)
        ? value.join(',')
        : String(value ?? '');
      await updateProviderConfig(providerId, {
        key,
        value: stringValue,
      });
      toast.success(`${CONFIG_LABELS[key] ?? key} updated`);
    } catch (err: any) {
      toast.error(`Failed to update ${CONFIG_LABELS[key] ?? key}`);
    }
  };

  const handleChange = (key: string, value: any, immediate = false) => {
    setConfig((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        value,
      },
    }));

    if (immediate) {
      saveToServer(key, value);
    } else {
      if (debounceTimers.current[key]) {
        clearTimeout(debounceTimers.current[key]);
      }
      debounceTimers.current[key] = setTimeout(() => {
        saveToServer(key, value);
      }, 800);
    }
  };

  const isBooleanField = (key: string, item?: any): boolean => {
    if (item?.type === 'BOOLEAN') return true;
    const val = item?.value;
    return (
      typeof val === 'boolean' ||
      val === 'true' ||
      val === 'false' ||
      key.startsWith('ENABLE_') ||
      key.startsWith('ALLOW_') ||
      key.startsWith('PORTAL_ALLOW_') ||
      key.startsWith('PORTAL_BYPASS_')
    );
  };

  const isNumericField = (key: string, item?: any): boolean => {
    if (item?.type === 'NUMBER') return true;
    return Boolean(CONFIG_UNITS[key]) || key.startsWith('MIN_') || key.startsWith('MAX_');
  };

  const inputCls =
    'rounded-gsm border border-stroke-soft-200 bg-white px-3 py-1.5 text-sm text-text-strong-950 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

  if (loading) {
    return (
      <div className="rounded-gsm border border-stroke-soft-200 bg-white p-8 text-center text-text-sub-600">
        <div className="animate-spin inline-block size-6 border-2 border-primary border-t-transparent rounded-full mb-2" />
        <p className="text-sm">Loading configurations…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {PROVIDER_CONFIG_CATEGORIES.map((category) => (
        <div
          key={category.label}
          className="rounded-gsm border border-stroke-soft-200 bg-white overflow-hidden"
        >
          <div className="border-b border-stroke-soft-200 px-5 py-3.5 bg-bg-weak-50">
            <h3 className="text-sm font-bold tracking-tight text-text-strong-950">
              {category.label}
            </h3>
          </div>

          <div className="divide-y divide-stroke-soft-200">
            {category.keys.map((key) => {
              const item = config[key] || { value: '' };
              const val = item.value;
              const unit = CONFIG_UNITS[key];
              const selectOptions = SELECT_OPTIONS[key];
              const label = CONFIG_LABELS[key] || key.replace(/_/g, ' ');

              return (
                <div
                  key={key}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-5 py-3 hover:bg-tint transition-colors"
                >
                  <div className="flex-1 min-w-0 pr-4">
                    <p className="text-sm font-medium text-text-strong-950">
                      {label}
                    </p>
                    <p className="text-xs text-text-sub-600 font-mono">
                      {key}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {selectOptions ? (
                      <select
                        value={String(val ?? '')}
                        onChange={(e) => handleChange(key, e.target.value, true)}
                        className={`${inputCls} min-w-[180px]`}
                      >
                        <option value="">Select option</option>
                        {selectOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : isBooleanField(key, item) ? (
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={val === true || val === 'true'}
                          onChange={(e) =>
                            handleChange(key, e.target.checked, true)
                          }
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    ) : isNumericField(key, item) ? (
                      <div className="inline-flex items-center gap-1.5">
                        <input
                          type="number"
                          value={val !== undefined && val !== null ? String(val) : ''}
                          onChange={(e) => handleChange(key, e.target.value)}
                          className={`${inputCls} w-28 text-right font-mono`}
                        />
                        {unit && (
                          <span className="text-xs text-text-sub-600 font-mono">
                            {unit}
                          </span>
                        )}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={val !== undefined && val !== null ? String(val) : ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                        placeholder="Configuration value"
                        className={`${inputCls} min-w-[200px]`}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProviderConfigTab;
